import React, { useState, useRef, useEffect, useContext } from "react";
import { Modal, View, Text, Image, TouchableOpacity, Dimensions, Platform, StyleSheet, ImageBackground, TextInput, Button, ScrollView } from "react-native";
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { doc, updateDoc, getFirestore } from "firebase/firestore"; // Import Firestore to save image URL
import Draggable from "react-draggable";
import examplePhoto1 from "../assets/exPhoto1.jpg";
import examplePhoto2 from "../assets/exPhoto2.jpg";
import { SketchPicker } from 'react-color'; // Import a color picker library
import { useBackground } from '../Screens/Helpers/BackgroundContext';
import Cropper from 'react-easy-crop'; // Import the cropper
import { MYTASSEL_PLUS_FEATURES } from "../constants/MyTasselPlus";
import useMyTasselPlus from "../Hooks/useMyTasselPlus";
import {
	loadStandardProfileBackgrounds,
	FALLBACK_STANDARD_PROFILE_BACKGROUNDS,
	getImageSource,
	getStandardProfileBackgroundValue,
} from "../services/StandardProfileBackgroundService";

const { width, height } = Dimensions.get('window');
const isMobile = width < 768;
const isTablet = width >= 768 && width < 1024;
const isSmallDesktop = width >= 1024 && width <= 1500;
const isLargeDesktop = width > 1500;
const isSmallPhone = width <= 400;
const isTabletPortrait = isTablet && height > width;
const isTabletLandscape = isTablet && width > height;

const fontFamilies = {
	Bodoni72Smallcaps: "Bodoni72Smallcaps",
};

const STANDARD_FONT_COLORS = [
	{ label: "Black", value: "#000000" },
	{ label: "White", value: "#FFFFFF" },
	{ label: "Red", value: "#FF0000" },
	{ label: "Green", value: "#00AA00" },
	{ label: "Blue", value: "#0000FF" },
	{ label: "Yellow", value: "#FFD700" },
];

const GraduationCard = ({ pageOwner, currentUserData, isVisitorView }) => {
	const db = getFirestore();
	const { backgroundImage, setBackgroundImage } = useBackground();
	const myTasselPlus = useMyTasselPlus();

	const topRightImageRef = useRef(null);
	const bottomLeftImageRef = useRef(null);
	const [updatedPageOwner, setUpdatedPageOwner] = useState(pageOwner);
	const backgroundImageRef = useRef(null);
	const [textColor, setTextColor] = useState(pageOwner.gradCardTextColor || '#fff');
	const [selectedTextColor, setSelectedTextColor] = useState(pageOwner.gradCardTextColor || '#fff');
	const [feedbackMessage, setFeedbackMessage] = useState('');
	// Cache brightness results keyed by URL so we never re-scan the same image twice.
	const brightnessCache = useRef({});

	const getNameFontSize = () => {
		const fullName = `${pageOwner.firstName} ${pageOwner.lastName}`;
		const nameLength = fullName.length;

		// Avg char width factor for Bodoni72Smallcaps (~0.50 em/char).
		// Mobile uses the onLayout-measured container width so we always have the real value.
		// Desktop uses an estimate based on known card dimensions.
		const CHAR_FACTOR = 0.50;
		const fitToWidth = (designed, availableWidth) => {
			// Apply a small safety margin on web builds (Safari can clip tightly-rendered fonts).
			const SAFETY = Platform.OS === 'web' ? 0.92 : 1;
			const computed = Math.floor((availableWidth / (nameLength * CHAR_FACTOR)) * SAFETY);
			return Math.min(designed, computed);
		};

		// ── Small phone ───────────────────────────────────────────────────────
		// Scaled proportionally: ~52 / ~41 / ~31 for short / medium / long
		if (isSmallPhone) {
			const shortMax = 52;
			if (nameLength <= 14) return shortMax;
			return Math.round(Math.max(47 * (2 / 3), 47 * (14 / nameLength)));
		}

		// ── Mobile ────────────────────────────────────────────────────────────
		// Targets: ≤14 chars → ~50px, 16 chars → ~42px, 29 chars → ~22px.
		// Use a width-based clamp on mobile so web (Safari on iPhone) always fits.
		if (isMobile) {
			// Estimate available width on mobile (give small horizontal padding margin).
			const availableWidth = Math.max(120, Math.floor(width * 0.9) - 40);
			const shortDesired = 50;
			if (nameLength <= 14) return fitToWidth(shortDesired, availableWidth);
			const computed = Math.round(Math.max(18, 42 * Math.pow(16 / nameLength, 1.08)));
			return fitToWidth(computed, availableWidth);
		}

		// ── Tablet (card = 95vw, details right:70, no left constraint) ────────
		if (isTablet) {
			const shortMax = 100, baseMax = 90;
			const designed = nameLength <= 12 ? shortMax : Math.max(baseMax * 0.55, baseMax * (12 / nameLength));
			return fitToWidth(designed, width * 0.95 - 70);
		}

		// ── Desktop: "Luke Christopher" (17 chars) = reference size ──────────
		// Short names scale up (cap 1.65×), long names scale down (floor 0.55×).
		// Clamped so the name fits on one line: card is 90/95vw, details right:110.
		const REFERENCE_LENGTH = 17;
		const referenceSize = isSmallDesktop ? 100 : 118;
		const rawMultiplier = REFERENCE_LENGTH / nameLength;
		// Short names cap at 1.1× reference (≈110/130px) to prevent them towering over the card.
		// Medium/long names keep the original 1.65× cap.
		const maxMultiplier = nameLength <= 14 ? 1.1 : 1.65;
		const multiplier = Math.min(maxMultiplier, Math.max(0.55, rawMultiplier));
		const designed = Math.round(referenceSize * multiplier);
		const cardFraction = isSmallDesktop ? 0.90 : 0.95;
		return fitToWidth(designed, width * cardFraction - 110);
	};

	// Web-only: measure rendered text and apply a one-time safe reduction
	const nameTextRef = useRef(null);
	const [nameFontSizeOverride, setNameFontSizeOverride] = useState(null);
	const [crop, setCrop] = useState({ x: 0, y: 0 });
	const [zoom, setZoom] = useState(1);
	const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
	const [imageToCrop, setImageToCrop] = useState(null);
	const [isCropping, setIsCropping] = useState(false);
	const [topRightImage, setTopRightImage] = useState(null);
	const [bottomLeftImage, setBottomLeftImage] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const [position, setPosition] = useState(null);
	const [cropperKey, setCropperKey] = useState(0);
	const [originalFile, setOriginalFile] = useState(null);

	const [showPopup, setShowPopup] = useState(false);
	const [isBackgroundSelectorVisible, setIsBackgroundSelectorVisible] = useState(false);
	const [backgroundImageUrlInput, setBackgroundImageUrlInput] = useState('');
	const [errorMessage, setErrorMessage] = useState('');
	const [standardBackgrounds, setStandardBackgrounds] = useState(FALLBACK_STANDARD_PROFILE_BACKGROUNDS);
	const [standardBackgroundsLoading, setStandardBackgroundsLoading] = useState(false);
	const [showCustomColorPicker, setShowCustomColorPicker] = useState(false);

	useEffect(() => {
	}, [updatedPageOwner]);

	useEffect(() => {
		const savedColor = pageOwner?.gradCardTextColor || "#fff";
		setTextColor(savedColor);
		setSelectedTextColor(savedColor);
		setUpdatedPageOwner((currentOwner) => ({
			...(currentOwner || pageOwner),
			...pageOwner,
			gradCardTextColor: savedColor,
		}));
	}, [pageOwner]);

	useEffect(() => {
		const loadBackgrounds = async () => {
			setStandardBackgroundsLoading(true);
			const backgrounds = await loadStandardProfileBackgrounds(db);
			setStandardBackgrounds(backgrounds);
			setStandardBackgroundsLoading(false);
		};

		loadBackgrounds();
	}, [db]);

	// Measure on web and apply a single computed reduction if the text overflows.
	useEffect(() => {
		if (Platform.OS !== 'web') return;
		// Reset previous override when name or window width changes so measurement is fresh.
		setNameFontSizeOverride(null);
		const id = setTimeout(() => {
			const el = nameTextRef.current;
			if (!el) return;
			const clientW = el.clientWidth || el.offsetWidth;
			const scrollW = el.scrollWidth || el.offsetWidth;
			if (scrollW > clientW && clientW > 0) {
				const base = getNameFontSize();
				// Use a more conservative reduction factor to account for Safari subpixel clipping.
				const ratio = (clientW / scrollW) * 0.9;
				const newSize = Math.max(10, Math.floor(base * ratio) - 1);
				if (newSize < base) setNameFontSizeOverride(newSize);
			}
		}, 60); // allow layout
		return () => clearTimeout(id);
	}, [pageOwner.firstName, pageOwner.lastName, width, backgroundImage]);

	const handleImageUpload = async (file, pos, inputRef) => {
		if (file) {
			const reader = new FileReader();
			reader.onloadend = () => {
				if (originalFile && file.name === originalFile.name && file.size === originalFile.size) {
					setImageToCrop(null);
					setTimeout(() => {
						setImageToCrop(reader.result);
						setIsCropping(true);
						setCrop({ x: 0, y: 0 });
						setZoom(1);
						setPosition(pos);
						setCropperKey(prevKey => prevKey + 1);
					}, 0);
				} else {
					setOriginalFile(file);
					setImageToCrop(null);
					setTimeout(() => {
						setImageToCrop(reader.result);
						setIsCropping(true);
						setCrop({ x: 0, y: 0 });
						setZoom(1);
						setPosition(pos);
						setCropperKey(prevKey => prevKey + 1);
					}, 0);
				}
				if (inputRef && inputRef.current) {
					inputRef.current.value = '';
				}
			};
			reader.readAsDataURL(file);
		}
	};

	const handleCropComplete = async () => {
		if (!position) return;

		setIsLoading(true);

		try {
			const croppedImage = await getCroppedImg(imageToCrop, croppedAreaPixels);
			
			const storage = getStorage();
			const filename = `cropped_${pageOwner.uid}_${Date.now()}.jpg`;
			const fileRef = storageRef(storage, `graduationImages/${filename}`);

			if (position === 'topRight' && topRightImage) {
				const previousImageRef = storageRef(storage, topRightImage);
				await deleteObject(previousImageRef);
			} else if (position === 'bottomLeft' && bottomLeftImage) {
				const previousImageRef = storageRef(storage, bottomLeftImage);
				await deleteObject(previousImageRef);
			}

			const blob = await fetch(croppedImage).then(res => res.blob());
			await uploadBytes(fileRef, blob);
			const newImageUrl = await getDownloadURL(fileRef);

			const userDocRef = doc(db, "users", pageOwner.email);
			if (position === 'topRight') {
				setTopRightImage(newImageUrl);
				await updateDoc(userDocRef, {
					topRightGradCardImage: newImageUrl,
				});
				setUpdatedPageOwner(prev => ({ ...prev, topRightGradCardImage: newImageUrl }));
			} else {
				setBottomLeftImage(newImageUrl);
				await updateDoc(userDocRef, {
					bottomLeftGradCardImage: newImageUrl,
				});
				setUpdatedPageOwner(prev => ({ ...prev, bottomLeftGradCardImage: newImageUrl }));
			}
		} catch (error) {
			alert("Failed to upload cropped image. Please try again.");
		} finally {
			setIsLoading(false);
			setIsCropping(false);
		}
	};

	const getCroppedImg = (imageSrc, pixelCrop) => {
		return new Promise((resolve, reject) => {
			const image = new window.Image();
			image.src = imageSrc;
			image.onload = () => {
				const canvas = document.createElement('canvas');
				const ctx = canvas.getContext('2d');
				canvas.width = pixelCrop.width;
				canvas.height = pixelCrop.height;
				ctx.drawImage(
					image,
					pixelCrop.x,
					pixelCrop.y,
					pixelCrop.width,
					pixelCrop.height,
					0,
					0,
					pixelCrop.width,
					pixelCrop.height
				);
				canvas.toBlob((blob) => {
					resolve(URL.createObjectURL(blob));
				}, 'image/jpeg');
			};
			image.onerror = (error) => reject(error);
		});
	};

	const handleBackgroundImageChange = async (event) => {
		if (!myTasselPlus.canUseFeature(MYTASSEL_PLUS_FEATURES.CUSTOM_BACKGROUND)) {
			event.target.value = '';
			myTasselPlus.openUpsell(MYTASSEL_PLUS_FEATURES.CUSTOM_BACKGROUND);
			return;
		}

		const file = event.target.files[0];
		if (!file) return;

		const storage = getStorage();
		const filename = `background_${pageOwner.uid}_${Date.now()}`;
		const fileRef = storageRef(storage, `graduationBackgrounds/${filename}`);

		try {
			const uploadTaskSnapshot = await uploadBytes(fileRef, file);
			const newBackgroundImageUrl = await getDownloadURL(uploadTaskSnapshot.ref);
			setBackgroundImage(newBackgroundImageUrl);

			const userDocRef = doc(db, "users", pageOwner.email);
			await updateDoc(userDocRef, {
				gradCardBackgroundImage: newBackgroundImageUrl,
			});
		} catch (error) {
			alert("Failed to upload background image. Please try again.");
		}
	};

	const handleSelectExampleBackground = async (background) => {
		const backgroundValue = getStandardProfileBackgroundValue(background);
		if (!backgroundValue) return;

		setBackgroundImage(backgroundValue);

		const userDocRef = doc(db, "users", pageOwner.email);
		await updateDoc(userDocRef, {
			gradCardBackgroundImage: backgroundValue,
		});
	};

	const handleBackgroundChangeByUrl = async () => {
		if (!myTasselPlus.canUseFeature(MYTASSEL_PLUS_FEATURES.CUSTOM_BACKGROUND)) {
			myTasselPlus.openUpsell(MYTASSEL_PLUS_FEATURES.CUSTOM_BACKGROUND);
			return;
		}

		if (!backgroundImageUrlInput) {
			setErrorMessage('Please enter a valid URL.');
			return;
		}
		try {
			setBackgroundImage(backgroundImageUrlInput);

			const userDocRef = doc(db, "users", pageOwner.email);
			await updateDoc(userDocRef, {
				gradCardBackgroundImage: backgroundImageUrlInput,
			});
			setBackgroundImageUrlInput('');
			setIsBackgroundSelectorVisible(false);
			setErrorMessage('');
		} catch (error) {
			setErrorMessage('Failed to update background image. Please try again.');
		}
	};

	const handleCloseBackgroundSelector = () => {
		setIsBackgroundSelectorVisible(false);
		setErrorMessage('');
	};

	const handleChangeTopRightImage = () => {
		topRightImageRef.current.click();
	};

	const handleChangeBottomLeftImage = () => {
		bottomLeftImageRef.current.click();
	};

	const getAverageBrightness = async (imageUrl) => {
		return new Promise((resolve, reject) => {
			const img = new window.Image();
			img.crossOrigin = "Anonymous";
			img.src = imageUrl;
			img.onload = () => {
				const canvas = document.createElement('canvas');
				const ctx = canvas.getContext('2d');
				canvas.width = img.width;
				canvas.height = img.height;
				ctx.drawImage(img, 0, 0);
				const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
				const data = imageData.data;
				let total = 0;
				let count = 0;
				for (let i = 0; i < data.length; i += 4) {
					let avg = (data[i] + data[i + 1] + data[i + 2]) / 3;
					total += avg;
					count++;
				}
				resolve(total / count);
			};
			img.onerror = (error) => reject(error);
		});
	};

	const setTextColorsBasedOnBackground = async (backgroundImageUri) => {
		const savedTextColor = updatedPageOwner?.gradCardTextColor || pageOwner.gradCardTextColor;
		if (savedTextColor) {
			setTextColor(savedTextColor);
			setSelectedTextColor(savedTextColor);
			return;
		}
		// Return cached result immediately — avoids a full pixel scan on every bg change.
		if (brightnessCache.current[backgroundImageUri] !== undefined) {
			setTextColor(brightnessCache.current[backgroundImageUri] > 128 ? '#4c956c' : '#e4e4e4');
			return;
		}
		const brightness = await getAverageBrightness(backgroundImageUri);
		brightnessCache.current[backgroundImageUri] = brightness;
		setTextColor(brightness > 128 ? '#4c956c' : '#e4e4e4');
	};

	const applyTextColor = async () => {
		const userDocRef = doc(db, "users", pageOwner.email);
		try {
			await updateDoc(userDocRef, {
				gradCardTextColor: selectedTextColor,
			});
			setTextColor(selectedTextColor);
			setUpdatedPageOwner(prev => ({ ...prev, gradCardTextColor: selectedTextColor }));
			setFeedbackMessage('Text color applied successfully!');
			setTimeout(() => setFeedbackMessage(''), 3000);
		} catch (error) {
			alert("Failed to update text color. Please try again.");
		}
	};

	const selectStandardTextColor = (color) => {
		setSelectedTextColor(color);
		setTextColor(color);
		setShowCustomColorPicker(false);
	};

	const openCustomColorPicker = () => {
		myTasselPlus.requireFeature(MYTASSEL_PLUS_FEATURES.CUSTOM_COLORS, () => {
			setShowCustomColorPicker(isVisible => !isVisible);
		});
	};

	useEffect(() => {
		if (backgroundImage) {
			setTextColorsBasedOnBackground(backgroundImage);
		}
	}, [backgroundImage]);

	// Compute final font size (allow override on web if measurement required)
	const computedNameFontSize = nameFontSizeOverride || getNameFontSize();
	const cardBackgroundSource = getImageSource(backgroundImage) || getImageSource(getStandardProfileBackgroundValue(standardBackgrounds[0]));

	return (
		<View style={styles.card}>
			<ImageBackground source={cardBackgroundSource} style={styles.backgroundImage}>
				<input type="file" ref={backgroundImageRef} style={{ display: "none" }} onChange={handleBackgroundImageChange} accept="image/*" />
				<input type="file" ref={topRightImageRef} style={{ display: "none" }} onChange={(e) => handleImageUpload(e.target.files[0], 'topRight', topRightImageRef)} accept="image/*" />
				<input type="file" ref={bottomLeftImageRef} style={{ display: "none" }} onChange={(e) => handleImageUpload(e.target.files[0], 'bottomLeft', bottomLeftImageRef)} accept="image/*" />

				{/* Centered text overlay for mobile */}
				{isMobile ? (
					<View style={styles.textOverlayContainer}>
						<Text style={[styles.classOfText, { color: textColor }]}>Class of</Text>
						<Text style={[styles.classOfYear, { color: textColor }]}>{pageOwner.graduationYear}</Text>
						<View style={styles.detailsCentered}>
					<Text
						ref={nameTextRef}
						style={[
							styles.name,
							{
								color: textColor,
								fontSize: computedNameFontSize,
								lineHeight: computedNameFontSize * 1.15,
								...(Platform.OS === 'web' ? { whiteSpace: 'nowrap' } : {}),
							}
						]}
						numberOfLines={Platform.OS !== 'web' ? 1 : undefined}
						adjustsFontSizeToFit={Platform.OS !== 'web'}
					>
						{pageOwner.firstName} {pageOwner.lastName}
					</Text>
							{pageOwner?.graduationDay ? (
								<Text style={[styles.dateAndOrganization, { color: textColor }]}>Graduation Date: {pageOwner.graduationMonth} {pageOwner.graduationDay}, {pageOwner.graduationYear}</Text>
							) : (
								<Text style={[styles.dateAndOrganization, { color: textColor }]}>Graduation Date: {pageOwner.graduationMonth} {pageOwner.graduationYear}</Text>
							)}
							<Text style={[styles.dateAndOrganization, { color: textColor }]}>{pageOwner.organization.name}</Text>
						</View>
					</View>
				) : (
					<>
						<Text style={[styles.classOfText, { color: textColor }]}>Class of</Text>
						<Text style={[styles.classOfYear, { color: textColor }]}>{pageOwner.graduationYear}</Text>
						<View style={styles.details}>
						<Text
							ref={nameTextRef}
							style={[
								styles.name,
								{
									color: textColor,
									fontSize: computedNameFontSize,
									lineHeight: computedNameFontSize * 1.15,
									...(Platform.OS === 'web' ? { whiteSpace: 'nowrap' } : {}),
								}
							]}
							numberOfLines={Platform.OS !== 'web' ? 1 : undefined}
							adjustsFontSizeToFit={Platform.OS !== 'web'}
						>
							{pageOwner.firstName} {pageOwner.lastName}
						</Text>
							{pageOwner?.graduationDay ? (
								<Text style={[styles.dateAndOrganization, { color: textColor }]}>Graduation Date: {pageOwner.graduationMonth} {pageOwner.graduationDay}, {pageOwner.graduationYear}</Text>
							) : (
								<Text style={[styles.dateAndOrganization, { color: textColor }]}>Graduation Date: {pageOwner.graduationMonth} {pageOwner.graduationYear}</Text>
							)}
							<Text style={[styles.dateAndOrganization, { color: textColor }]}>{pageOwner.organization.name}</Text>
						</View>
					</>
				)}

				{/* Display the top right image */}
					<Image
						source={{ uri: updatedPageOwner?.topRightGradCardImage || examplePhoto1 }}
						style={styles.topRightImageContainer}
						resizeMode="cover"
						shouldRasterizeIOS={true}
						renderToHardwareTextureAndroid={true}
					/>

				{/* Display the bottom left image */}
					<Image
						source={{ uri: updatedPageOwner?.bottomLeftGradCardImage || examplePhoto2 }}
						style={styles.bottomLeftImageContainer}
						resizeMode="cover"
						shouldRasterizeIOS={true}
						renderToHardwareTextureAndroid={true}
					/>

					{currentUserData?.uid === pageOwner.uid && !isVisitorView && (
						<>
							<TouchableOpacity style={styles.uploadBackgroundButton} onPress={() => setIsBackgroundSelectorVisible(true)}>
								<Text style={styles.uploadBackgroundButtonText}>Announcement Background & Settings</Text>
							</TouchableOpacity>

							<TouchableOpacity style={[styles.changeImageButton, styles.changeImageBottomLeftButton]} onPress={handleChangeBottomLeftImage}>
								<Text style={styles.changeImageButtonText}>{isMobile ? 'Change Bottom Image' : 'Change Bottom Left Image'}</Text>
							</TouchableOpacity>
							<TouchableOpacity style={[styles.changeImageButton, styles.changeImageTopRightButton]} onPress={handleChangeTopRightImage}>
								<Text style={styles.changeImageButtonText}>{isMobile ? 'Change Top Image' : 'Change Top Right Image'}</Text>
							</TouchableOpacity>

							{isBackgroundSelectorVisible && (
								<View style={styles.backgroundSelector}>
									<TouchableOpacity style={styles.closeButton} onPress={handleCloseBackgroundSelector}>
										<Text style={styles.closeButtonText}>X</Text>
									</TouchableOpacity>

									<ScrollView style={styles.settingsScroll} contentContainerStyle={styles.settingsContent}>
										<Text style={styles.settingsTitle}>Announcement Background & Settings</Text>
										<Text style={styles.settingsSectionTitle}>Standard Backgrounds</Text>
										{standardBackgroundsLoading ? (
											<Text style={styles.settingsMutedText}>Loading backgrounds...</Text>
										) : (
											<View style={styles.exampleBackgroundsContainer}>
												{standardBackgrounds.map((bg) => {
													const backgroundValue = getStandardProfileBackgroundValue(bg);
													const isSelected = backgroundImage === backgroundValue;
													return (
														<TouchableOpacity
															key={bg.id}
															onPress={() => handleSelectExampleBackground(bg)}
															style={[styles.exampleBackground, isSelected && styles.exampleBackgroundSelected]}
														>
															<Image source={getImageSource(backgroundValue)} style={styles.exampleBackgroundThumb} />
															<Text style={styles.exampleBackgroundText}>{bg.label}</Text>
														</TouchableOpacity>
													);
												})}
											</View>
										)}

										<Text style={styles.settingsSectionTitle}>Custom Background</Text>
										<TextInput
											style={styles.urlInput}
											onChangeText={setBackgroundImageUrlInput}
											value={backgroundImageUrlInput}
											placeholder="Enter background image URL"
										/>
										<View style={styles.settingsActionRow}>
											<TouchableOpacity style={styles.greenButton} onPress={handleBackgroundChangeByUrl}>
												<Text style={styles.buttonText}>Use URL {!myTasselPlus.hasPlus ? "+" : ""}</Text>
											</TouchableOpacity>
											<TouchableOpacity style={styles.greenButton} onPress={() => backgroundImageRef.current.click()}>
												<Text style={styles.buttonText}>Upload From Device {!myTasselPlus.hasPlus ? "+" : ""}</Text>
											</TouchableOpacity>
										</View>
										{errorMessage ? <Text style={styles.errorText}>{errorMessage}</Text> : null}

										<Text style={styles.settingsSectionTitle}>Announcement Text Color</Text>
										<View style={styles.selectedColorRow}>
											<View style={[styles.selectedColorSwatch, { backgroundColor: selectedTextColor }]} />
											<Text style={styles.selectedColorText}>{selectedTextColor.toUpperCase()}</Text>
										</View>
										<View style={styles.standardColorGrid}>
											{STANDARD_FONT_COLORS.map((color) => (
												<TouchableOpacity
													key={color.value}
													style={[
														styles.standardColorOption,
														selectedTextColor?.toLowerCase() === color.value.toLowerCase() && styles.standardColorOptionSelected,
													]}
													onPress={() => selectStandardTextColor(color.value)}
												>
													<View style={[styles.standardColorSwatch, { backgroundColor: color.value }]} />
													<Text style={styles.standardColorLabel}>{color.label}</Text>
												</TouchableOpacity>
											))}
										</View>
										<TouchableOpacity style={styles.greenButton} onPress={openCustomColorPicker}>
											<Text style={styles.buttonText}>Custom Color {!myTasselPlus.hasPlus ? "+" : ""}</Text>
										</TouchableOpacity>
										{showCustomColorPicker && myTasselPlus.hasPlus && (
											<View style={styles.colorPickerShell}>
												<SketchPicker
													color={selectedTextColor}
													onChangeComplete={(color) => setSelectedTextColor(color.hex)}
													onChange={(color) => setTextColor(color.hex)}
												/>
											</View>
										)}
										<TouchableOpacity style={styles.greenButton} onPress={applyTextColor}>
											<Text style={styles.buttonText}>Apply Text Color</Text>
										</TouchableOpacity>
										{feedbackMessage ? <Text style={styles.feedbackMessage}>{feedbackMessage}</Text> : null}
									</ScrollView>
								</View>
							)}
							</>
						)}
				{myTasselPlus.upsellModal}

				{isCropping && (
					<Modal visible={isCropping} transparent={false} animationType="slide">
						<View style={styles.instructionsModalContainer}>
							<Text style={styles.instructionText}>Scroll to zoom in or out.</Text>
							<Cropper
								key={cropperKey}
								image={imageToCrop}
								crop={crop}
								zoom={zoom}
								aspect={2/1}
								onCropChange={setCrop}
								onZoomChange={setZoom}
								onCropComplete={(croppedArea, croppedAreaPixels) => {
									setCroppedAreaPixels(croppedAreaPixels);
								}}
								style={{ width: '100%', height: '100%' }}
							/>

							{isLoading && (
								<View style={styles.loadingOverlay}>
									<Text style={styles.loadingText}>Uploading Image...</Text>
								</View>
							)}

							<View style={styles.buttonContainer}>
								<Button style={styles.button} title="Cancel" onPress={() => setIsCropping(false)} />
								<Button style={styles.button} title="Confirm Crop" onPress={handleCropComplete} />
							</View>
						</View>
					</Modal>
				)}
			</ImageBackground>
		</View>
	);
};

const styles = StyleSheet.create({
	topRightImageContainer: {
		width: isMobile || isTabletPortrait ? "80%" : "50%",
		height: isMobile || isTabletPortrait ? "40%" : "60%",
		position: "absolute",
		top: isMobile || isTabletPortrait ? 10 : 15,
		left: isMobile || isTabletPortrait ? "10%" : "50%",
		borderWidth: 5,
		borderColor: "#e4e4e4",
		transform: [{ rotate: "4deg" }],
		zIndex: 96,
		// Shadows on rotated elements force GPU compositing on iOS Safari — use native only.
		...Platform.select({
			native: { shadowOffset: { width: 7, height: 5 }, shadowRadius: 6, shadowColor: "rgba(0, 0, 0, 0.9)" },
			default: {},
		}),
		overflow: "hidden",
	},
	bottomLeftImageContainer: {
		width: isMobile || isTabletPortrait ? "80%" : "55%",
		height: isMobile || isTabletPortrait ? "40%" : "60%",
		position: "absolute",
		bottom: isMobile || isTabletPortrait ? 60 : 0,
		left: isMobile || isTabletPortrait ? "10%" : 20,
		borderWidth: 5,
		borderColor: "#e4e4e4",
		transform: [{ rotate: "-2deg" }],
		...Platform.select({
			native: { shadowOffset: { width: 7, height: 3 }, shadowRadius: 6, shadowColor: "rgba(0, 0, 0, 0.4)" },
			default: {},
		}),
		overflow: "hidden",
	},
	topRightImage: {
		width: "100%",
		height: "100%",
		position: "absolute",
		transform: [{ rotate: "4deg" }], // Slight angle to the right
		zIndex: 96,
		overflow: "hidden",
	},
	bottomLeftImage: {
		width: "100%",
		height: "100%",
		position: "absolute",
		transform: [{ rotate: "-2deg" }], // Slight angle to the right
		overflow: "hidden",
		// zIndex: 97
	},
	uploadBackgroundButton: {
		position: isMobile ? "absolute" : "relative",
		width: isMobile ? "100%" : isTablet ? "70%" : isSmallDesktop ? "50%" : "50%",
		height: isMobile ? 40 : isTablet ? "5%" : isSmallDesktop ? "5%" : "5%",
		justifyContent: "center",
		alignItems: "center",
		backgroundColor: "rgba(0,0,0,0.6)",
		top: 0,
		left: 0,
		borderRadius: isMobile ? 0 : 5,
		zIndex: 200,
		padding: 10,
	},
	uploadBackgroundButtonText: {
		color: "#fff",
		fontSize: isMobile ? 14 : isTablet ? 16 : isSmallDesktop ? 18 : 20,
	},
	backgroundSelector: {
		position: 'absolute',
		width: '100%',
		height: '100%',
		backgroundColor: 'rgba(0,0,0,0.72)',
		justifyContent: 'center',
		alignItems: 'center',
		zIndex: 1001,
		paddingHorizontal: isMobile ? 10 : 20,
		paddingVertical: isMobile ? 56 : 24,
	},
	urlInput: {
		borderWidth: 1,
		borderColor: '#ccc',
		padding: 10,
		marginVertical: 8,
		width: '100%',
		backgroundColor: '#fff',
		borderRadius: 8,
	},
	card: {
		flex: 1,
		alignItems: "center",
		justifyContent: "center",
		backgroundColor: "#fff",
		overflow: "hidden",
		borderWidth: 0,
		borderRadius: isMobile ? 15 : isTablet ? 20 : isSmallDesktop ? 25 : 30,
		borderColor: "#e4e4e4",
		marginBottom: isMobile ? 10 : isTablet ? 15 : isSmallDesktop ? 20 : 25,
		marginTop: isMobile ? 10 : isTablet ? 15 : isSmallDesktop ? 20 : 25,
		shadowOffset: { width: 0, height: 6 },
		shadowOpacity: 0.25,
		shadowRadius: 3.84,
	},
	backgroundImage: {
		width: "100%",
		height: "100%",
		resizeMode: "cover",
	},
	content: {
		flex: 1,
		padding: 20,
		alignItems: "center",
		justifyContent: "space-between",
	},
	classOfText: {
		alignSelf: isMobile || isTablet ? 'center' : 'flex-start',
		fontSize: isSmallPhone ? 32 : isMobile ? 38 : isTablet ? 50 : isSmallDesktop ? 40 : 70,
		color: "#e4e4e4",
		fontFamily: "Snell Roundhand",
		fontWeight: "bold",
		position: 'absolute',
		top: isMobile ? '35%' : isTablet ? '37%' : (isSmallDesktop ? 20 : 20), 
		left: isMobile || isTablet ? undefined : (isSmallDesktop ? 50 : 60),
		textShadowOffset: { width: 3, height: 3 },
		textShadowRadius: 6,
		textShadowColor: "rgba(0, 0, 0, 0.95)",
		zIndex: 99,
	},
	classOfYear: {
		alignSelf: isMobile || isTablet ? 'center' : 'flex-start',
		fontSize: isSmallPhone ? 90 : isMobile ? 120 : isTablet ? 200 : isSmallDesktop ? 175 : 250,
		color: "#e4e4e4",
		fontFamily: "Snell Roundhand",
		fontWeight: "bold",
		position: 'absolute',
		top: isMobile ? '37%' : isTablet ? '37%' : (isSmallDesktop ? 25 : 40), 
		left: isMobile || isTablet ? undefined : (isSmallDesktop ? 40 : 50),
		zIndex: 99,
		textShadowOffset: { width: 2, height: 2 },
		textShadowRadius: 3,
		textShadowColor: "rgba(0, 0, 0, 0.5)",
	},
	details: {
		alignItems: "center",
		position: "absolute",
		bottom: isMobile ? 225 : isTablet ? 70 : isSmallDesktop ? 40 : 50,
		right: isMobile ? 0 : isTablet ? 70 : isSmallDesktop ? 110 : 110,
		textShadowOffset: { width: 2, height: 2 },
		textShadowRadius: 3,
		textShadowColor: "rgba(0, 0, 0, 0.75)",
		zIndex: 99,
		width: isMobile ? "100%" : isTablet ? "50%" : isSmallDesktop ? "42%" : "45%",
		paddingHorizontal: isMobile ? 0 : isTablet ? 15 : 20,
	},
	detailsCentered: {
		alignItems: 'center',
		marginTop: 'auto',
		marginBottom: isMobile ? 20 : 20,
		paddingHorizontal: isMobile ? 25 : 20,
		width: isMobile ? '100%' : 'auto',
	},
	name: {
		color: "#e4e4e4",
		fontWeight: "bold",
		fontFamily: fontFamilies.Bodoni72Smallcaps,
		textShadowOffset: { width: 2, height: 2 },
		textShadowRadius: 3,
		textShadowColor: "rgba(0, 0, 0, 0.75)",
		textAlign: "center",
		alignSelf: 'center',
		maxWidth: isMobile ? '90%' : isTablet ? '90%' : '100%',
		overflow: 'hidden',
	},
	dateAndOrganization: {
		fontSize: isSmallPhone ? 14 : isMobile ? 18 : isTablet ? 25 : isSmallDesktop ? 20 : 35,
		color: "#e4e4e4",
		fontFamily: "Bradley Hand",
		marginTop: isMobile ? 2 : isTablet ? 2 : isSmallDesktop ? 2 : 4,
		textShadow_offset: { width: 3, height: 5 },
		textShadow_radius: 10,
		textShadow_color: "rgba(0, 0, 0, 0.75)",
		textAlign: "center",
	},
	uploadButtonText: {
		color: "#fff",
		fontSize: 16,
	},
	exampleBackgroundsContainer: {
		flexDirection: 'row',
		flexWrap: 'wrap',
		justifyContent: 'center',
		gap: 8,
	},
	exampleBackground: {
		width: isMobile ? 92 : 112,
		padding: 6,
		backgroundColor: '#fff',
		borderRadius: 8,
		borderWidth: 2,
		borderColor: 'transparent',
		alignItems: 'center',
	},
	exampleBackgroundSelected: {
		borderColor: '#4c956c',
		backgroundColor: '#ecfdf5',
	},
	exampleBackgroundThumb: {
		width: '100%',
		height: 54,
		borderRadius: 6,
		marginBottom: 5,
	},
	exampleBackgroundText: {
		color: '#000',
		fontSize: 12,
		textAlign: 'center',
	},
	greenButton: {
		backgroundColor: '#4c956c',
		paddingVertical: 10,
		paddingHorizontal: 14,
		marginVertical: 6,
		borderRadius: 8,
		alignItems: 'center',
	},
	buttonText: {
		color: '#fff',
		textAlign: 'center',
	},
	errorText: {
		color: 'red',
		marginTop: 10,
	},
	closeButton: {
		position: 'absolute',
		top: 12,
		left: 12,
		backgroundColor: '#ff4545', // Red background for the close button
		width: 42,
		height: 42,
		borderRadius: 100, // Circular button
		justifyContent: 'center',
		alignItems: 'center',
		zIndex: 1002, // Ensure it's above other elements
	},
	closeButtonText: {
		color: '#fff',
		fontWeight: 'bold',
		fontSize: 16,
	},
	changeImageButton: {
		padding: 10,
		backgroundColor: "rgba(0,0,0,0.6)",
		position: 'absolute',
		color: "#fff",
		zIndex: 150,
	},
	changeImageTopRightButton: {
		right: 0,
		bottom: 0,
		width: isMobile ? "50%" : isTablet ? "50%" : isSmallDesktop ? "50%" : "50%",
	},
	changeImageBottomLeftButton: {
		left: 0,
		bottom: 0,
		width: isMobile ? "50%" : isTablet ? "50%" : isSmallDesktop ? "50%" : "50%",
	},
	changeImageButtonText: {
		color: "#fff",
		textAlign: "center",
		fontSize: isMobile ? 14 : isTablet ? 16 : isSmallDesktop ? 16 : 20,
	},
	feedbackMessage: {
		color: '#bbf7d0',
		fontSize: 16,
		marginTop: 10,
		textAlign: 'center',
	},
	settingsScroll: {
		width: '100%',
		maxWidth: 760,
		maxHeight: '100%',
		backgroundColor: 'rgba(254, 253, 226, 0.96)',
		borderRadius: 12,
	},
	settingsContent: {
		padding: isMobile ? 14 : 18,
		paddingTop: isMobile ? 46 : 18,
	},
	settingsTitle: {
		color: '#2c6e49',
		fontSize: isMobile ? 18 : 22,
		fontFamily: fontFamilies.Bodoni72Smallcaps,
		fontWeight: 'bold',
		textAlign: 'center',
		marginBottom: 10,
	},
	settingsSectionTitle: {
		color: '#2c6e49',
		fontSize: 16,
		fontWeight: '700',
		marginTop: 12,
		marginBottom: 8,
	},
	settingsMutedText: {
		color: '#64748b',
		textAlign: 'center',
		marginVertical: 8,
	},
	settingsActionRow: {
		flexDirection: isMobile ? 'column' : 'row',
		gap: 10,
	},
	selectedColorRow: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: 10,
		marginBottom: 10,
	},
	selectedColorSwatch: {
		width: 36,
		height: 36,
		borderRadius: 18,
		borderWidth: 2,
		borderColor: '#2c6e49',
	},
	selectedColorText: {
		color: '#2c6e49',
		fontWeight: '700',
	},
	standardColorGrid: {
		flexDirection: 'row',
		flexWrap: 'wrap',
		gap: 8,
		marginBottom: 8,
	},
	standardColorOption: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: 6,
		paddingVertical: 8,
		paddingHorizontal: 10,
		backgroundColor: '#fff',
		borderRadius: 8,
		borderWidth: 2,
		borderColor: 'transparent',
	},
	standardColorOptionSelected: {
		borderColor: '#4c956c',
		backgroundColor: '#ecfdf5',
	},
	standardColorSwatch: {
		width: 18,
		height: 18,
		borderRadius: 9,
		borderWidth: 1,
		borderColor: '#94a3b8',
	},
	standardColorLabel: {
		color: '#1f2937',
		fontSize: 13,
		fontWeight: '600',
	},
	colorPickerShell: {
		alignSelf: 'center',
		maxWidth: '100%',
		overflow: 'hidden',
		marginVertical: 8,
	},
	modalContainer: {
		flex: 1,
		justifyContent: 'center',
		alignItems: 'center',
		backgroundColor: 'rgba(0, 0, 0, 0.8)', // Semi-transparent background
		padding: 20,
	},
	buttonContainer: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		padding: 20,
		position: 'absolute', // Positioning the buttons absolutely
		bottom: 20, // Position them at the bottom of the modal
		left: 20, // Add some left margin
		right: 20, // Add some right margin
	},
	button: {
		flex: 1,
		marginHorizontal: 10,
		padding: 10,
		backgroundColor: '#4CAF50', // Green background color
		borderRadius: 5,
		color: '#fff', // White text color
		fontFamily: fontFamilies.Bodoni72Smallcaps,
	},
	instructionsModalContainer: {
		flex: 1,
		justifyContent: 'flex-start', // Align items to the top
		alignItems: 'center',
		backgroundColor: 'rgba(0, 0, 0, 0.8)', // Optional: dark background for better visibility
		padding: 20,
		borderRadius: 10, // Rounded corners for the modal
		margin: 20, // Margin to prevent touching the edges
		width: "100%",
		height: "100%",
	},
	instructionText: {
		color: '#fff', // White text for visibility
		textAlign: 'center',
		fontSize: 18, // Adjust font size for better readability
		padding: 10,
		fontFamily: fontFamilies.Bodoni72Smallcaps,
		position: 'absolute', // Fixed position
		top: 20, // Position it at the top
		left: '50%', // Center horizontally
		transform: [{ translateX: -50 }], // Adjust for centering
		zIndex: 1001, // Ensure it is above other elements
	},
	loadingOverlay: {
		position: 'absolute',
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		justifyContent: 'center',
		alignItems: 'center',
		backgroundColor: 'rgba(0, 0, 0, 0.5)', // Semi-transparent background
		zIndex: 1000, // Ensure it overlays above other content
	},
	loadingText: {
		color: '#fff', // White text for visibility
		fontSize: 20,
	},
	textOverlayContainer: {
		alignItems: 'center',
		zIndex: 101,
		flex: 1,
	},
	modalHeader: {
		fontSize: 20,
		fontWeight: 'bold',
		marginBottom: 10,
	},
});
export default GraduationCard;
